import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PropertySchemaType } from "~/common/types";
import { Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input } from "~/components/ui";
import {
  isProtectedColumn,
  type JsonDataSourcePayload,
  useDataSourceJsonEditor,
} from "~/features/dataImportProjects/hooks/useDataSourceJsonEditor";
import { parseGeoItemIdCell } from "~/features/dataImportProjects/utils/geoItemIdCell";
import { jsonCellText, resolveJsonCellEdit } from "~/features/dataImportProjects/utils/jsonCellValue";
import { v2Route } from "~/v2/routes";

type DataSourceJsonEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dataSource: { id: string; name: string };
  // Property schema associations from the detail panel: the name drives column order, the type
  // decides how a cell is edited and what a newly added column is persisted as.
  schemaColumns: { name: string; propertyTypeId: string }[];
  onSaved: () => void;
  readOnly: boolean;
};

type LoadedState = {
  schemas: string[][];
  rawRows: Record<string, unknown>[]; // loaded rows verbatim, original value types
  originalKeys: Set<string>[]; // per-row key presence in the loaded JSON
  s3Path: string;
};

type Column = { name: string; isNew: boolean; propertyTypeId: string | null };

// Column display order: the persisted JSON column order (union of `schemas` names
// and reserved keys present in rows, first-seen order), followed by schema-only
// columns not yet present in the JSON (visually marked as new).
const columnOrder = (loaded: LoadedState, schemaColumns: { name: string; propertyTypeId: string }[]): Column[] => {
  const typeOf = new Map(schemaColumns.map(({ name, propertyTypeId }) => [name, propertyTypeId]));
  const seen = new Set<string>();
  const ordered: Column[] = [];
  const push = (name: string, isNew: boolean) => {
    if (seen.has(name)) {
      return;
    }
    seen.add(name);
    ordered.push({ name, isNew, propertyTypeId: typeOf.get(name) ?? null });
  };
  for (const row of loaded.rawRows) {
    for (const key of Object.keys(row)) {
      push(key, false);
    }
  }
  for (const [name] of loaded.schemas) {
    push(name, false);
  }
  for (const { name } of schemaColumns) {
    push(name, true);
  }
  return ordered;
};

/**
 * Reads back what the importer will make of a linkGeoItems cell: the IDs it resolves against Miguel,
 * or the token it would reject. Pasting an ID is the only way to reach the GeoItem pipeline, and an
 * ID is unreadable, so the link is what makes a paste checkable before the job runs.
 */
function GeoItemIdCellHint({ value }: { value: string }) {
  const { t } = useTranslation();
  const parsed = useMemo(() => parseGeoItemIdCell(value), [value]);

  if (parsed.ids.length === 0 && parsed.invalid.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-12-regular">
      {parsed.ids.map((id) => (
        <a
          key={id}
          href={v2Route.geoItems.detail.$buildPath({ params: { id } })}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-fg-link underline underline-offset-2"
        >
          {id.slice(0, 8)}
        </a>
      ))}
      {parsed.invalid.length > 0 ? (
        <span className="text-danger">
          {t("feature.dataImportProjects.detail.dataSource.jsonEdit.invalidGeoItemId", {
            value: parsed.invalid.join(", "),
          })}
        </span>
      ) : null}
    </div>
  );
}

export function DataSourceJsonEditDialog({
  open,
  onOpenChange,
  dataSource,
  schemaColumns,
  onSaved,
  readOnly,
}: DataSourceJsonEditDialogProps) {
  const { t } = useTranslation();
  const { load, save, loading, saving } = useDataSourceJsonEditor(dataSource.id);
  const [loaded, setLoaded] = useState<LoadedState | null>(null);
  const [rows, setRows] = useState<Record<string, string>[]>([]);

  useEffect(() => {
    setLoaded(null);
    setRows([]);
    if (!open) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const result = await load();
      if (cancelled || !result) {
        return;
      }
      const rawRows = result.jsonData.rows;
      setLoaded({
        schemas: result.jsonData.schemas,
        rawRows,
        originalKeys: rawRows.map((row) => new Set(Object.keys(row))),
        s3Path: result.s3Path,
      });
      setRows(rawRows.map((row) => Object.fromEntries(Object.entries(row).map(([k, v]) => [k, jsonCellText(v)]))));
    })();
    return () => {
      cancelled = true;
    };
  }, [open, load, dataSource.id]);

  const columns = useMemo(() => (loaded ? columnOrder(loaded, schemaColumns) : []), [loaded, schemaColumns]);

  const onCellChange = useCallback((rowIndex: number, name: string, value: string) => {
    setRows((prev) => prev.map((row, i) => (i === rowIndex ? { ...row, [name]: value } : row)));
  }, []);

  const isInvalidCell = useCallback(
    (rowIndex: number, name: string) =>
      loaded !== null && !resolveJsonCellEdit(loaded.rawRows[rowIndex]?.[name], rows[rowIndex]?.[name] ?? "").ok,
    [loaded, rows],
  );
  const hasInvalidCell = useMemo(
    () => rows.some((row, rowIndex) => Object.keys(row).some((name) => isInvalidCell(rowIndex, name))),
    [rows, isInvalidCell],
  );

  const onSave = useCallback(async () => {
    if (readOnly || !loaded || hasInvalidCell) {
      return;
    }
    const addedColumns = columns
      .filter((c) => c.isNew)
      .filter(({ name }) => rows.some((row) => (row[name] ?? "") !== ""));
    // Columns the backend will accept: schema-backed (persisted or from the detail panel).
    const knownColumns = new Set([...loaded.schemas.map(([name]) => name), ...schemaColumns.map((c) => c.name)]);
    const payload: JsonDataSourcePayload = {
      schemas: [
        ...loaded.schemas,
        // The importer reads the column type from here, so a column added in this dialog has to
        // carry the type its property schema declares rather than a default.
        ...addedColumns.map(({ name, propertyTypeId }) => [name, propertyTypeId ?? "string"]),
      ],
      // Start from the raw original row verbatim (all keys, original value types, reserved
      // keys untouched) and overlay only cells the operator actually edited, so untouched
      // non-string/null values (e.g. reserved `page: null`) round-trip unchanged.
      rows: rows.map((editedRow, i) => {
        const rawRow = loaded.rawRows[i];
        const result: Record<string, unknown> = { ...rawRow };
        for (const [key, value] of Object.entries(editedRow)) {
          if (loaded.originalKeys[i].has(key)) {
            const edit = resolveJsonCellEdit(rawRow[key], value);
            if (edit.ok) {
              result[key] = edit.value;
            }
          } else if (value !== "" && knownColumns.has(key)) {
            result[key] = value;
          }
        }
        return result;
      }),
    };
    if (await save(payload, loaded.s3Path)) {
      onSaved();
      onOpenChange(false);
    }
  }, [readOnly, loaded, hasInvalidCell, columns, rows, schemaColumns, save, onSaved, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-6xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {t("feature.dataImportProjects.detail.dataSource.jsonEdit.title", { name: dataSource.name })}
          </DialogTitle>
        </DialogHeader>
        {loading || !loaded ? (
          <p className="text-12-regular text-fg-3">{t("ui.processing")}</p>
        ) : rows.length === 0 ? (
          <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>
        ) : (
          <div className="flex-1 overflow-auto rounded-[var(--radius-md)] border border-border-default">
            <table className="w-full min-w-max border-collapse text-12-regular">
              <thead className="bg-bg-subtle text-fg-2">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.name}
                      className="whitespace-nowrap border-b border-border-default px-3 py-2 text-left font-semibold"
                    >
                      {column.name}
                      {column.isNew ? (
                        <Badge className="ml-1.5" tone="default">
                          {t("feature.dataImportProjects.detail.dataSource.jsonEdit.newColumn")}
                        </Badge>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  // Row identity/order is immutable in this editor, so the index key is stable.
                  <tr key={rowIndex} className="border-t border-border-default first:border-t-0">
                    {columns.map((column) =>
                      isProtectedColumn(column.name) ? (
                        <td key={column.name} className="whitespace-nowrap px-3 py-2 text-fg-3">
                          {row[column.name] ?? ""}
                        </td>
                      ) : (
                        <td key={column.name} className="px-2 py-1">
                          <Input
                            value={row[column.name] ?? ""}
                            onChange={(e) => onCellChange(rowIndex, column.name, e.target.value)}
                            className="h-8 min-w-32"
                            disabled={readOnly}
                            aria-invalid={isInvalidCell(rowIndex, column.name)}
                          />
                          {isInvalidCell(rowIndex, column.name) ? (
                            <p className="mt-1 text-12-regular text-danger">
                              {t("feature.dataImportProjects.detail.dataSource.jsonEdit.invalidJson")}
                            </p>
                          ) : null}
                          {column.propertyTypeId === PropertySchemaType.LinkGeoItems ? (
                            <GeoItemIdCellHint value={row[column.name] ?? ""} />
                          ) : null}
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("ui.cancel")}
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={saving || loading || rows.length === 0 || readOnly || hasInvalidCell}
          >
            {t("ui.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
